import { materialCells, materialRenderers } from "@skrice.studio/material-renderers";
import { Generate, rankWith, uiTypeIs } from "@jsonforms/core";
import { JsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import {
    Button,
    CardHeader,
    CircularProgress,
    FormControl,
    Grid,
    Paper,
    TextareaAutosize,
    TextField
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import { Buffer } from "buffer";
import { createForm } from "rc-form";
import * as React from "react";
import ReactJson from "react-json-view";
import { Config } from "../../config/config";
import { getGlobalVar, Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface Props {
    activeUser: any;
    activeEnv: any;
    classes: any;
    form: any;
    match: any;
}

interface State {
    loaded: boolean;
    fileKey: string;
    filepath: string;
    jsonSchema: any;
    jsonSchemaKey: string;
    uiSchema: any;
    uiSchemaKey: string;
    content: any;
    rawValue: string;
    toggleRawJson: boolean;
}

const GAME_ITEMS = getGlobalVar("ITEMS").map(item => {
    const name = Helpers.getLocalizationName(item.name);

    return {
        ...item,
        name,
        firstLetter: item.type.replace("_", " ").toUpperCase(),
    };
});

export const GET_ITEM_NAME_BY_ID = (itemId: any) => {
    const item = GAME_ITEMS.find(gameItem => gameItem.id === itemId);
    return item ? item.name : itemId;
}

const ItemControl = (control) => {
    const handleChange = (_e, newItem) => {
        control.handleChange(control.path, newItem.id);
    };

    const getOptionLabel = (option) => {
        if (typeof option === "number" || typeof option === "string") {
            const item = GAME_ITEMS.find(_item => _item.id === option);

            return item?.name || "Missing";
        }

        return option.name;
    };

    return <FormControl style={{
        marginRight: 8,
        width: "100%",
        marginBottom: 10,
    }}>
        <Autocomplete
            onChange={handleChange}
            value={control.data}
            options={GAME_ITEMS.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
            groupBy={option => option.firstLetter}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option.id === value}
            size="small"
            renderInput={(params) =>
                <TextField {...params} label={control.label} id={`reward_select_${control.path}`} variant="outlined" size="small"/>}
        />
    </FormControl>;
};

const renderers = [
    ...materialRenderers,

    {tester: rankWith(3, uiTypeIs("GameItems")), renderer: withJsonFormsControlProps(ItemControl)}
];

class ItemEditorUpdateComponent extends React.Component<Props, State> {

    private mounted: boolean;
    private updateTimeout: any;

    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            fileKey: this.props.match.params.key,
            filepath: (Buffer.from(this.props.match.params.key, "base64").toString("ascii")),
            jsonSchema: {},
            jsonSchemaKey: "",
            uiSchema: {},
            uiSchemaKey: "",
            content: {},
            rawValue: "",
            toggleRawJson: false,
        };

        document.title = Helpers.documentTitle(`Item Editor - ${this.state.filepath}`);
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    componentDidMount() {
        this.mounted = true;
        this.fetchData();
    }

    saveFile = async ({formData}, _e) => {
        try {
            await axios.post(
                `${Config.getInstance.apiUrl}/api/item_editor/${this.state.fileKey}`,
                {
                    content: formData,
                },
                this.headers(),
            );
            PopupMessage.success("File saved!");
        } catch (e) {
            PopupMessage.error(e.response?.data.message || e.message);
        }
    };

    saveError(errors) {
        errors.forEach(err => {
            PopupMessage.error(`Form error: ${err.stack}`, 10);
        });
    }

    async fetchData() {
        try {
            const response = await axios.get(`${Config.getInstance.apiUrl}/api/item_editor/${this.state.fileKey}`, this.headers());
            if (this.mounted) {
                this.setState({
                    ...response.data.data,
                });
                this.setState({
                    rawValue: JSON.stringify(this.state.content, null, "  "),
                });

                let schema = this.state.jsonSchema;
                let uiSchema = this.state.uiSchema;

                if (Object.keys(schema).length <= 0) {
                    schema = Generate.jsonSchema(this.state.content);
                }

                if (Object.keys(uiSchema).length <= 0) {
                    uiSchema = Generate.uiSchema(schema);
                }

                this.setState({
                    jsonSchema: schema,
                    uiSchema
                });
            }
        } catch (e) {
            PopupMessage.error(e.response?.data.message || e.message);
        }

        if (this.mounted) {
            this.setState({
                loaded: true,
            });
        }
    }

    changeRawJson = (e) => {
        this.setState({
            rawValue: e.target.value,
        });
        clearTimeout(this.updateTimeout);
        this.updateTimeout = setTimeout(() => {
            try {
                this.setState({
                    content: JSON.parse(this.state.rawValue),
                });
            } catch (e) {
                PopupMessage.error("Unable to parse JSON value: " + e.message);
            }
        }, 500);
    };

    saveRawJson = () => {
        const json = JSON.parse(this.state.rawValue);
        this.saveFile({
            formData: json,
        }, null);
    };

    formatRawJson = () => {
        const json = JSON.parse(this.state.rawValue);
        this.setState({
            rawValue: JSON.stringify(json, null, "  "),
        });
    };

    headers() {
        return {
            headers: {Authorization: Helpers.getSessionId()},
            timeout: 30000
        };
    }

    renderForm() {
        if (!this.state.toggleRawJson) {
            return <JsonForms
                schema={this.state.jsonSchema}
                uischema={this.state.uiSchema}
                data={this.state.content}
                renderers={renderers}
                cells={materialCells}
                // onChange={({errors, data}) => setJsonformsData(data)}
            />;
        } else {
            return (
                <Grid xs={12} style={{overflow: "auto"}}>
                    <Grid xs={6} style={{float: "left", width: "50%"}}>
                        <TextareaAutosize
                            style={{width: "100%"}}
                            value={this.state.rawValue}
                            onChange={this.changeRawJson}
                        />
                        <Button disableElevation
                            variant="contained"
                            color="primary"
                            onClick={this.formatRawJson}
                        >
                            Format JSON
                        </Button>
                        <Button disableElevation
                            variant="contained"
                            onClick={this.saveRawJson}
                            color="default"
                        >
                            Save
                        </Button>
                    </Grid>
                    <Grid xs={6} style={{float: "left", width: "50%"}}>
                        <ReactJson
                            src={this.state.content}
                        />
                    </Grid>
                </Grid>
            );
        }
    }

    renderSchemaActions() {
        const items = [];

        if (!!this.state.jsonSchemaKey) {
            items.push((
                <Button
                    disableElevation
                    key="raw-json"
                    onClick={() => this.setState({toggleRawJson: !this.state.toggleRawJson})}
                    variant="contained"
                    size="small"
                >
                    Toggle RAW JSON
                </Button>
            ));
        }
        return (
            <React.Fragment>
                {items}
            </React.Fragment>
        );
    }

    render() {
        if (!this.state.loaded) {
            return <div className="contentLoading">
                <CircularProgress/>
            </div>;
        }

        return (
            <div className="contentPadding">
                <Paper style={{padding: "10px"}} elevation={0} className="json-editor">
                    <CardHeader
                        title={`File: ${this.state.filepath}`}
                        action={
                            this.renderSchemaActions()
                        }
                    />
                    {this.renderForm()}
                </Paper>
            </div>
        );
    }
}

const styles: any = theme => ({
    root: {
        width: "100%",
        marginTop: 24,
        overflow: "hidden",
    },
});

export const ItemEditorUpdate = createForm()(withStyles(styles)(ItemEditorUpdateComponent));
